import type {
  VoiceProvider,
  VoiceProviderPresets,
  VoiceProviderSettings,
  VoiceSettingKey,
} from './types';

export const VOICE_PROVIDER_PRESETS = [
  '11labs',
  'hamsa',
  'deepgram',
  'soniox',
  'grok-tts',
  'qwen3-tts',
  'openai',
  'azure',
] as const satisfies readonly VoiceProvider[];

/** Providers whose language is a free code passed through the runtime language mapper. */
export const LANGUAGE_CODE_PROVIDERS = [
  'soniox',
  'grok-tts',
] as const satisfies readonly VoiceProvider[];

// Finite offered choices from the task's provider docs; these do not validate raw JSON.
// Soniox/Grok take free language codes through the shared runtime mapper, so they have no list.

type BasicPresetField = 'voiceId' | 'model' | 'language';

/** Providers whose runtime has no default voice, so a voice ID must be set. */
export const VOICE_ID_REQUIRED_PROVIDERS = ['openai'] as const satisfies readonly VoiceProvider[];

/** Providers that never read the top-level voice ID (Hamsa uses `speaker`). */
export const VOICE_ID_UNUSED_PROVIDERS = ['hamsa'] as const satisfies readonly VoiceProvider[];

/** English names for provider language codes; values outside this map show as they are. */
export const LANGUAGE_LABELS: Readonly<Record<string, string>> = {
  eng: 'English',
  arb: 'Arabic',
  ukr: 'Ukrainian',
};

/** Top-level Voice fields are not part of the provider settings DTOs, so they have their own map. */
export const BASIC_PRESETS: Partial<
  Record<VoiceProvider, Partial<Record<BasicPresetField, readonly string[]>>>
> = {
  '11labs': {
    // An unset model lets the runtime pick one by language, shown as "Automatic".
    model: ['eleven_turbo_v2_5', 'eleven_multilingual_v2'],
    language: ['eng', 'arb', 'ukr'],
  },
  soniox: { model: ['tts-rt-v1-preview'] },
  'qwen3-tts': {
    model: [
      'Qwen/Qwen3-TTS-12Hz-1.7B-CustomVoice',
      'Qwen/Qwen3-TTS-12Hz-1.7B-VoiceDesign',
      'Qwen/Qwen3-TTS-12Hz-1.7B-Base',
    ],
    language: ['English', 'Chinese', 'Auto'],
  },
  openai: {
    voiceId: [
      'alloy',
      'ash',
      'ballad',
      'coral',
      'echo',
      'fable',
      'nova',
      'onyx',
      'sage',
      'shimmer',
      'verse',
      'marin',
      'cedar',
    ],
    model: ['gpt-4o-mini-tts', 'gpt-4o-mini-tts-2025-12-15', 'tts-1', 'tts-1-hd'],
  },
};

export const VOICE_PRESETS = {
  '11labs': {},
  hamsa: {
    speaker: [
      'ASSY',
      'Ahmed',
      'Aisha',
      'Ali',
      'Alia',
      'Amir',
      'Amjad',
      'Barbara',
      'Carla',
      'Dalal',
      'Deema',
      'Dima',
      'Eman',
      'Emma',
      'Fady',
      'Fahd',
      'Faisal',
      'Faiza',
      'Fatma',
      'Gassan',
      'Ghazal',
      'Hady',
      'Hamdan',
      'Hatem',
      'Hiba',
      'Hind',
      'Jaber',
      'James',
      'Jasem',
      'Kamla',
      'Khadiga',
      'Khadija',
      'Khalid',
      'Lana',
      'Layan',
      'Layla',
      'Lyali',
      'Maha',
      'Mai',
      'Mais',
      'Majd',
      'Majid',
      'Mansour',
      'Mariam',
      'Marwa',
      'Mazen',
      'Nabil',
      'Nada',
      'Noor',
      'Noura',
      'Nouran',
      'Ola',
      'Othman',
      'Raghad',
      'Rahma',
      'Rami',
      'Rema',
      'Renat',
      'Roger',
      'Ruba',
      'Safa',
      'Sahar',
      'Salam',
      'Salem',
      'Salma',
      'Salwa',
      'Saly',
      'Samir',
      'Sarah',
      'Sayed',
      'Talin',
      'Tamim',
      'Yehya',
    ],
    dialect: [
      'pls',
      'leb',
      'jor',
      'syr',
      'ksa',
      'bah',
      'uae',
      'egy',
      'irq',
      'qat',
      'kuw',
      'oma',
      'msa',
      'ar-sa',
      'en',
    ],
    sampleRate: [8000, 16000],
  },
  deepgram: {},
  soniox: {
    audioFormat: [
      'pcm_f32le',
      'pcm_s16le',
      'wav',
      'pcm_mulaw',
      'pcm_alaw',
      'flac',
      'mp3',
      'opus',
      'aac',
    ],
  },
  'grok-tts': {},
  'qwen3-tts': {
    taskType: ['CustomVoice', 'VoiceDesign', 'Base'],
    responseFormat: ['wav', 'mp3', 'flac', 'pcm', 'opus'],
  },
  openai: {
    responseFormat: ['mp3', 'opus', 'aac', 'flac', 'wav', 'pcm'],
  },
  azure: {},
} as const satisfies VoiceProviderPresets;

type SonioxFormat = NonNullable<VoiceProviderSettings['soniox']['audioFormat']>;
type SonioxRate = NonNullable<VoiceProviderSettings['soniox']['sampleRate']>;

const SONIOX_RATES: Record<string, readonly SonioxRate[]> = {
  pcm_f32le: [8000, 16000, 24000, 44100, 48000],
  pcm_s16le: [8000, 16000, 24000, 44100, 48000],
  wav: [8000, 16000, 24000, 44100, 48000],
  pcm_mulaw: [8000],
  pcm_alaw: [8000],
  flac: [16000, 24000, 44100, 48000],
  mp3: [16000, 24000, 32000, 44100, 48000],
  opus: [8000, 16000, 24000, 48000],
  aac: [16000, 24000, 44100, 48000],
} satisfies Record<SonioxFormat, readonly SonioxRate[]>;

export const sonioxSampleRates = (format: unknown): readonly SonioxRate[] => {
  if (format === undefined || format === null) return SONIOX_RATES.pcm_s16le;
  return typeof format === 'string' && Object.hasOwn(SONIOX_RATES, format)
    ? SONIOX_RATES[format]
    : [];
};

/**
 * Every setting a provider's dedicated Advanced controls own. A provider switch clears all of
 * them so values such as `speed` or `instructions` never carry over with another meaning.
 */
export const PROVIDER_SETTING_KEYS = {
  '11labs': ['stability', 'similarityBoost', 'style', 'useSpeakerBoost', 'streamingLatency'],
  hamsa: ['speaker', 'dialect', 'mulaw', 'speed', 'expressiveness', 'sampleRate', 'normalizeAudio'],
  deepgram: ['instructionsPreset'],
  soniox: ['audioFormat', 'sampleRate', 'bitrate'],
  'grok-tts': [],
  'qwen3-tts': ['taskType', 'instructions', 'refAudio', 'refText', 'responseFormat'],
  openai: ['speed', 'instructions', 'responseFormat'],
  azure: [],
} as const satisfies { [P in VoiceProvider]: readonly (keyof VoiceProviderSettings[P])[] };

export const providerSettingKeys = (provider: string): readonly VoiceSettingKey[] =>
  Object.hasOwn(PROVIDER_SETTING_KEYS, provider)
    ? PROVIDER_SETTING_KEYS[provider as VoiceProvider]
    : [];
